import {
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core'
import { Close } from '@material-ui/icons'
import React, { useState } from 'react'
import {
  useTranslate,
  sanitizeFetchType,
  useRefresh,
  useMutation,
  useNotify,
} from 'react-admin'
import { ResourceName } from '../../../core/ResourceName'
import Button from '../../common/customized-mui-components/Button'
import DraggableComponent from '../../common/DraggableComponent'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogProperty: {
      marginBottom: theme.spacing(1),
    },
    dialogActions: {
      height: '16px',
      background: 'rgba(0,0,0,0.2)',
      minWidth: '300px',
      userSelect: 'none',
    },
    dialogTitle: {
      paddingLeft: '10px',
      width: '100%',
    },
    dialogCloseButton: {
      margin: 0,
    },
    actionsButton: {
      margin: '2%',
      width: '96%',
    },
  }),
)

export const ConfirmDialog = ({
  confirm,
  cancel,
  source,
  open,
  action,
}: {
  confirm: () => void
  cancel: () => void
  source: string
  open: boolean
  action: 'set' | 'remove'
}) => {
  const translate = useTranslate()
  return (
    <Dialog open={open}>
      <DialogTitle>
        {translate(`resources.users.dialogs.confirm.${source}.${action}`)}
      </DialogTitle>
      <DialogActions>
        <Button
          variant="contained"
          onClick={confirm}
          label={translate('ra.action.save')}
          useSmallVersionBreakpoint={false}
        />
        <Button
          variant="contained"
          onClick={cancel}
          label={translate('const.cancel')}
          useSmallVersionBreakpoint={false}
        />
      </DialogActions>
    </Dialog>
  )
}

export const ChangePasswordDialog = ({
  open,
  close,
}: {
  open: boolean
  close: () => void
}) => {
  const classes = useStyles()
  const [value, setValue] = useState<string>('')
  const [confirmValue, setConfirmValue] = useState<string>('')
  const [error, setError] = useState(false)
  const [errorText, setErrorText] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [mutate] = useMutation()
  const refresh = useRefresh()
  const translate = useTranslate()
  const notify = useNotify()

  const handleClose = () => {
    setValue('')
    setError(false)
    setErrorText('')
    close()
  }

  const handleValidate = () => {
    setError(false)
    setErrorText('')
    if (value !== confirmValue) {
      setError(true)
      setErrorText(
        translate(
          'resources.current-user.dialogs.changePassword.password.error-not-matching',
        ),
      )
    }
    if (value.replace(/\s/g, '').length === 0) {
      setError(true)
      setErrorText(
        translate(
          'resources.current-user.dialogs.changePassword.password.error-bad-string',
        ),
      )
    }
    if (value.replace(/\s/g, '').length !== value.length) {
      setError(true)
      setErrorText(
        translate(
          'resources.current-user.dialogs.changePassword.password.error-no-space',
        ),
      )
    }
    // if (!/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z]{8,}$/.test(value)) {
    //   setError(true)
    //   setErrorText(
    //     'resources.users.dialogs.changePassword.error-password-requirements',
    //   )
    // } else if (value.replace(/^[a-zA-Z0-9]*$/, '').length !== 0) {
    //   setError(true)
    //   setErrorText('resources.users.dialogs.changePassword.error-bad-string')
    // } else
    if (!error) setConfirmOpen(true)
  }

  const handleChangeUserPassword = async () => {
    setConfirmOpen(false)
    await mutate(
      {
        type: sanitizeFetchType('changePassword'),
        resource: ResourceName.CURRENT_USER,
        payload: {
          newPassword: value,
        },
      },
      {
        returnPromise: true,
        onFailure: (err) => notify(err?.message, 'error'),
      },
    )
    setValue('')
    refresh()
    close()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <ConfirmDialog
        open={confirmOpen}
        cancel={() => setConfirmOpen(false)}
        confirm={handleChangeUserPassword}
        source="changePassword"
        action="set"
      />
      <DialogActions
        className={classes.dialogActions}
        style={{ cursor: 'move' }}
      >
        <Typography className={classes.dialogTitle} id="draggable-dialog-title">
          {translate('resources.users.dialogs.changePassword.title')}
        </Typography>
        <IconButton
          size="small"
          className={classes.dialogCloseButton}
          onClick={close}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      <DialogContent>
        <TextField
          className={classes.dialogProperty}
          variant="outlined"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          fullWidth
          error={error}
          helperText={translate(errorText)}
          type="password"
          label={translate('resources.users.fields.password')}
        />
        <TextField
          className={classes.dialogProperty}
          variant="outlined"
          value={confirmValue}
          onChange={(e) => setConfirmValue(e.target.value)}
          fullWidth
          type="password"
          label={translate('resources.users.fields.confirmPassword')}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={handleValidate}
          label={translate('ra.action.save')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
        />
        <Button
          variant="contained"
          onClick={handleClose}
          label={translate('const.cancel')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
        />
      </DialogActions>
    </Dialog>
  )
}
